import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSocketStore } from '../../stores/useSocketStore';
import { useVehicleConflictStore } from '../../stores/useVehicleConflictStore';
import { usePermissions } from '../auth/usePermissions';

export default function useEnsureVehicleValidation({
  tripId,
  codigoIntegracao,
  timeCaught,
  vehicle,
  startAt,
  endAt,
  lock = true
}) {

  const socket = useSocketStore(state => state.socket);

  const payloadRef = useRef(null);

  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [vehicleConflicts, setVehicleConflicts] = useState(null);
  const [hasConflictVehicle, setHasConflictVehicle] = useState(false);
  const [validated, setValidated] = useState(false);

  const { canLockVehicles } = usePermissions();

  const payload = useMemo(() => {

    if (
      !tripId ||
      !vehicle ||
      vehicle === 'NEWSEL' ||
      !startAt ||
      !endAt
    ) {
      return null;
    }

    return {
      tripId,
      codigoIntegracao,
      timeCaught,
      vehicle,
      startAt,
      endAt
    };

  }, [
    tripId,
    codigoIntegracao,
    timeCaught,
    vehicle,
    startAt,
    endAt
  ]);

  useEffect(() => {

    payloadRef.current = payload;

    if (!payload) {
      setVehicleConflicts(null);
      setHasConflictVehicle(false);
    }

  }, [payload]);

  const checkVehicle = useCallback(() => {

    if (!canLockVehicles) {
      return;
    }

    if (!socket?.connected || !payloadRef.current) {
      return;
    }

    socket.emit('vehicle-lock:check', payloadRef.current, (response) => {
      // console.log('vehicle-lock:check', response);
      setVehicleConflicts(response?.conflicts || []);
      setHasConflictVehicle(response?.conflict || false);
      setLoadingVehicle(false);
      setValidated(true);
    });

  }, [socket, canLockVehicles]);

  const upsertVehicle = useCallback(() => {

    if (!canLockVehicles) {
      return;
    }

    if (!socket?.connected || !payloadRef.current) {
      return;
    }

    socket.emit('vehicle-lock:upsert', payloadRef.current, (response) => {
      // console.log('vehicle-lock:upsert', response);
      setVehicleConflicts(response?.conflicts || []);
      setHasConflictVehicle(response?.conflict || false);
      setLoadingVehicle(false);
      setValidated(true);
    });

  }, [socket, canLockVehicles]);

  useEffect(() => {

    if (!canLockVehicles) {
      setValidated(true);
      setLoadingVehicle(false);
      setVehicleConflicts([]);
      setHasConflictVehicle(false);
      return;
    }

    if (!payload) {
      return;
    }

    setValidated(false);
    setLoadingVehicle(true);
    const timeout = setTimeout(() => {
      if (lock) {
        upsertVehicle();
      } else {
        checkVehicle();
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
    }

  }, [
    payload,
    lock,
    upsertVehicle,
    checkVehicle,
    canLockVehicles
  ]);

  useEffect(() => {

    if (!canLockVehicles) {
      return;
    }

    if (!socket || !tripId) {
      return;
    }

    const handleLocksChanged = () => {
      checkVehicle();
    };

    socket.on('vehicle-locks:changed', handleLocksChanged);

    return () => {
      socket.off('vehicle-locks:changed', handleLocksChanged);
    };

  }, [
    tripId,
    socket,
    checkVehicle,
    canLockVehicles
  ]);

  useEffect(() => {

      const store = useVehicleConflictStore.getState();

      if (store.loadingConflicts !== loadingVehicle) {
          store.setLoadingConflicts(loadingVehicle);
      }

  }, [loadingVehicle]);

  useEffect(() => {

    return () => {

      if (!canLockVehicles) {
        return;
      }

      if (!socket?.connected) {
        return;
      }

      if (!lock|| !tripId) {
        return;
      }

      socket.emit('vehicle-lock:release', {tripId});

    };

  }, [
    tripId,
    lock,
    socket,
    canLockVehicles
  ]);

  useEffect(() => {

    if (!canLockVehicles || !socket) {
      return;
    }

    const handleDisconnect = () => {

      if (!payloadRef.current) {
        return;
      }

      setValidated(false);
      setLoadingVehicle(true);

    };

    const handleConnect = () => {

      if (!payloadRef.current) {
        return;
      }

      setValidated(false);
      setLoadingVehicle(true);

      if (lock) {
        upsertVehicle();
      } else {
        checkVehicle();
      }

    };

    socket.on('disconnect', handleDisconnect);
    socket.on('connect', handleConnect);

    return () => {

      socket.off('disconnect', handleDisconnect);
      socket.off('connect', handleConnect);

    };

  }, [
      socket,
      lock,
      canLockVehicles,
      upsertVehicle,
      checkVehicle
  ]);

  return {
    validated,
    loadingVehicle,
    vehicleConflicts,
    hasConflictVehicle,
    setVehicleConflicts
  };
}